import { Theme } from '@mui/material/styles';
import { SuperClusterAlgorithm, GridAlgorithm, NoopAlgorithm, type Renderer } from '@googlemaps/markerclusterer';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TurnSlightRightIcon from '@mui/icons-material/TurnSlightRight';
import { SvgIconComponent } from '@mui/icons-material';
import { TripEventLocationDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';

/**
 * Determines the fill color for a cluster marker based on event count and theme.
 * @param {number} count - The number of events in the cluster.
 * @param {Theme} theme - The theme object containing color palette definitions.
 * @returns {string} The hex color code for the cluster marker fill.
 *                   - Returns error color if count >= 101
 *                   - Returns warning color if count >= 11
 *                   - Returns info color otherwise
 */
const getClusterFillColor = (count: number, theme: Theme): string => {
	if (count >= 101) return theme.palette.error.main;
	if (count >= 11) return theme.palette.warning.main;
	return theme.palette.info.main;
};

/**
 * Builds a custom Google Maps cluster icon with concentric rings and a core circle.
 * The icon size scales logarithmically with the cluster count, and the rings fade
 * progressively from the outside inward.
 *
 * @param count - The number of items in the cluster, used to determine the icon size
 * @param theme - The theme object containing color values for styling the cluster icon
 * @returns A Google Maps Icon object with the generated cluster icon as a data URL,
 *          including size, anchor point, and label origin positioning
 *
 * @example
 * const icon = buildClusterIcon(42, theme);
 * marker.setIcon(icon);
 */
const buildClusterIcon = (count: number, theme: Theme): google.maps.Icon => {
	const fillColor = getClusterFillColor(count, theme);
	const coreRadius = 8 + Math.min(Math.log2(count) * 1, 6); // was 14 + ... * 3, 10
	const rings = 2; // was 4
	const ringSpacing = 5; // was 10
	const totalRadius = coreRadius + rings * ringSpacing;
	const size = totalRadius * 2;

	const canvas = document.createElement('canvas');
	canvas.width = size;
	canvas.height = size;
	const ctx = canvas.getContext('2d')!;
	const cx = totalRadius;
	const cy = totalRadius;

	for (let i = rings; i >= 1; i--) {
		const ringRadius = coreRadius + i * ringSpacing;
		const opacity = Math.round((0.08 + (rings - i) * 0.06) * 255)
			.toString(16)
			.padStart(2, '0');

		ctx.beginPath();
		ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
		ctx.fillStyle = fillColor + opacity;
		ctx.fill();
	}

	ctx.beginPath();
	ctx.arc(cx, cy, coreRadius, 0, Math.PI * 2);
	ctx.fillStyle = fillColor;
	ctx.fill();

	ctx.strokeStyle = 'rgba(255,255,255,0.9)';
	ctx.lineWidth = 2;
	ctx.stroke();

	return {
		url: canvas.toDataURL(),
		scaledSize: new google.maps.Size(size, size),
		anchor: new google.maps.Point(totalRadius, totalRadius),
		labelOrigin: new google.maps.Point(totalRadius, totalRadius),
	};
};

/**
 * Builds a clustering algorithm based on the specified type.
 * @param type - The type of clustering algorithm to build. Can be 'supercluster', 'grid', or 'noop'.
 * @returns A clustering algorithm instance configured for the specified type.
 * @example
 * const algorithm = buildAlgorithm('supercluster');
 */
export const buildAlgorithm = (type: 'supercluster' | 'grid' | 'noop') => {
	switch (type) {
		case 'grid':
			return new GridAlgorithm({
				maxZoom: 14, // stop clustering above this zoom level
				gridSize: 60, // pixel size of each grid cell
			});
		case 'noop':
			return new NoopAlgorithm({});
		case 'supercluster':
		default:
			return new SuperClusterAlgorithm({
				maxZoom: 14, // stop clustering above this zoom level
				radius: 80, // pixel radius within which markers are grouped
			});
	}
};

/**
 * Builds a renderer function for map markers that creates clustered marker icons.
 * @param {Theme} theme - The theme object used to style the cluster icons
 * @returns {Renderer} A renderer object with a render method that creates Google Maps markers
 * @deprecated Google Maps Marker is deprecated. Consider using AdvancedMarkerElement instead.
 *
 * @example
 * const renderer = buildRenderer(myTheme);
 * const marker = renderer.render({ count: 5, position: { lat: 40.7128, lng: -74.0060 } });
 */
export const buildRenderer = (theme: Theme): Renderer => ({
	render({ count, position }) {
		return new google.maps.Marker({
			position,
			zIndex: 50 + count,
			label: {
				text: String(count),
				color: '#FFFFFF',
				fontWeight: 'bold',
				fontSize: '11px',
			},
			icon: buildClusterIcon(count, theme),
		});
	},
});

/**
 * Returns the appropriate theme color based on event severity
 */
export const getEventColor = (severity: string | undefined, theme: Theme): string => {
	switch (severity?.toUpperCase()) {
		case 'HIGH':
		case 'SERIOUS':
		case 'CRITICAL':
			return theme.palette.error.main;
		case 'MEDIUM':
			return theme.palette.warning.main;
		case 'LOW':
		default:
			return theme.palette.success.main;
	}
};

/**
 * Returns the appropriate MUI icon component based on event type
 */
export const getEventIcon = (eventType: string | undefined): SvgIconComponent => {
	switch (eventType?.toUpperCase()) {
		case 'SPEEDING':
		case 'SPEED':
			return SpeedIcon;
		case 'HARSH_ACCELERATION':
		case 'ACCELERATION':
			return TrendingUpIcon;
		case 'HARSH_BRAKING':
		case 'BRAKING':
			return TrendingDownIcon;
		case 'HARSH_CORNERING':
		case 'CORNERING':
			return TurnSlightRightIcon;
		default:
			return WarningIcon;
	}
};

/**
 * Formats event type from API format to human-readable format
 * e.g., "HARSH_BRAKING" -> "Harsh Braking"
 */
export const formatEventType = (eventType: string | undefined): string => {
	if (!eventType) return 'Unknown Event';

	return eventType
		.toLowerCase()
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

/**
 * Returns human-readable severity label
 */
export const getSeverityLabel = (severity: string | undefined): string => {
	switch (severity?.toUpperCase()) {
		case 'CRITICAL':
			return 'Critical';
		case 'SERIOUS':
			return 'Serious';
		case 'HIGH':
			return 'High';
		case 'MEDIUM':
			return 'Medium';
		case 'LOW':
			return 'Low';
		default:
			return 'Unknown';
	}
};

/**
 * Returns a warning message based on event type for high severity events
 */
export const getEventWarningMessage = (eventType: string | undefined): string => {
	switch (eventType?.toUpperCase()) {
		case 'SPEEDING':
		case 'SPEED':
			return 'Some instance of hard braking detected. Try to anticipate earlier to avoid hard braking.';
		case 'HARSH_BRAKING':
		case 'BRAKING':
			return 'Hard braking detected. Try to anticipate earlier to avoid hard braking.';
		case 'HARSH_ACCELERATION':
		case 'ACCELERATION':
			return 'Harsh acceleration detected. Try to accelerate more gradually.';
		case 'HARSH_CORNERING':
		case 'CORNERING':
			return 'Harsh cornering detected. Try to slow down before corners.';
		default:
			return 'Driving event detected that may affect your score.';
	}
};

/**
 * Formats time from Date to "HH:MM" format
 */
export const formatEventTime = (date: Date | undefined): string => {
	if (!date) return '--:--';
	const d = new Date(date);
	return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Formats event location from coordinates to readable string
 */
export const formatEventLocation = (location: TripEventLocationDto | undefined): string => {
	if (location?.latitude === undefined || location?.longitude === undefined) {
		return 'Location unavailable';
	}
	return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
};

/**
 * Formats duration from milliseconds to readable string
 */
export const formatEventDuration = (durationMs: number | undefined): string => {
	if (!durationMs || durationMs <= 0) return '0s';

	const seconds = Math.floor(durationMs / 1000);
	if (seconds < 60) {
		return `${seconds}s`;
	}

	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;
	if (remainingSeconds === 0) {
		return `${minutes}m`;
	}
	return `${minutes}m ${remainingSeconds}s`;
};
